import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import {
    CodeType,
    CodeTypeReasonInfo,
    Filter,
    FilterType,
    IRtnTripAsNumber,
    LogLocationType,
    SortType,
} from '@/types/logs'
import { OfficialReportRequestBody } from '@/types/officialReport'
import { MechanicalInformationRequestBody } from '@/types/mechanicalInformation'
import { Log } from '@/types/logs'

interface State {
    sort: SortType
    filter: Filter
    multipleSelection: Log[]
}

interface Actions {
    setSorting: (type: SortType) => void
    setFilter: (filter: FilterType, values?: string[]) => void
    setFilterCodesMap: (codesMap: Record<string, CodeType>) => void
    setMultipleSelection: (logs: Log[]) => void
    clearFilters: () => void
}

export const useCombinedLogsTableStateStore = create<State & Actions>()(
    persist(
        (set) => ({
            sort: SortType.Time,
            filter: {
                type: FilterType.All,
                codesMap: {},
            },
            multipleSelection: [],
            setSorting: (sort: SortType) => set({ sort }),
            setFilter: (type: FilterType, value?: string[]) =>
                set((prev) => ({
                    filter: {
                        ...prev.filter,
                        type,
                        value,
                    },
                })),
            setFilterCodesMap: (codesMap: Record<string, CodeType>) =>
                set((prev) => ({
                    filter: {
                        ...prev.filter,
                        codesMap,
                    },
                })),
            setMultipleSelection: (logs: Log[]) => set({ multipleSelection: logs }),
            clearFilters: () =>
                set({
                    filter: {
                        type: FilterType.DailyLog,
                        codesMap: {},
                    },
                    multipleSelection: [],
                }),
        }),
        {
            name: 'combined-logs-table-storage',
        }
    )
)

interface FormState {
    selectedRowId: string | null
    code: string
    route: string
    trip: string
    locationType: LogLocationType | null
    selectedTrips: IRtnTripAsNumber[]
    selectedOriginalTrips: IRtnTripAsNumber[]
    reason: CodeTypeReasonInfo | null
    report: OfficialReportRequestBody | null
    mechanicalInfo: MechanicalInformationRequestBody | null
    isBulkCurtailment: boolean
    nightShiftDate: string | null
}

interface FormActions {
    setSelectedRowId: (id: string | null) => void
    setCode: (code: string) => void
    setRoute: (route: string) => void
    setTrip: (trip: string) => void
    setLocationType: (type: LogLocationType) => void
    setSelectedTrips: (trips: IRtnTripAsNumber[]) => void
    setSelectedOriginalTrips: (trips: IRtnTripAsNumber[]) => void
    setReason: (reason: CodeTypeReasonInfo | null) => void
    setReport: (report: OfficialReportRequestBody | null) => void
    setMechanicalInformation: (reason: MechanicalInformationRequestBody | null) => void
    setIsBulkCurtailmentLog: (isBulkCurtailment: boolean) => void
    setNightShiftDate: (nightShiftDate: string | null) => void
    clearInputValues: () => void
}

export const useCombinedLogsTableFormStateStore = create<FormState & FormActions>()((set) => ({
    selectedRowId: null,
    code: '',
    route: '',
    trip: '',
    locationType: null,
    selectedTrips: [],
    selectedOriginalTrips: [],
    reason: null,
    report: null,
    mechanicalInfo: null,
    isBulkCurtailment: false,
    nightShiftDate: null,
    setSelectedRowId: (id: string | null) => set({ selectedRowId: id }),
    setCode: (code: string) => set({ code }),
    setRoute: (route: string) =>
        set({
            route,
        }),
    setTrip: (trip: string) =>
        set({
            trip,
        }),
    setLocationType: (type: LogLocationType) => set({ locationType: type }),
    setSelectedTrips: (trips: IRtnTripAsNumber[]) =>
        set({
            selectedTrips: trips,
        }),
    setSelectedOriginalTrips: (trips: IRtnTripAsNumber[]) =>
        set({
            selectedOriginalTrips: trips,
        }),
    setReason: (reason: CodeTypeReasonInfo | null) =>
        set({
            reason,
        }),
    setReport: (report: OfficialReportRequestBody | null) =>
        set({
            report,
        }),
    setMechanicalInformation: (mechanicalInfo: MechanicalInformationRequestBody | null) =>
        set({
            mechanicalInfo,
        }),
    setIsBulkCurtailmentLog: (isBulkCurtailment: boolean) =>
        set({
            isBulkCurtailment,
        }),
    setNightShiftDate: (nightShiftDate: string | null) => set({ nightShiftDate }),
    clearInputValues: () => {
        set({
            code: '',
            route: '',
            trip: '',
            locationType: null,
            selectedTrips: [],
            selectedOriginalTrips: [],
            reason: null,
            isBulkCurtailment: false,
            mechanicalInfo: null,
            report: null,
            nightShiftDate: null,
        })
    },
}))
